"use client";

import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Check, ChevronRight, ChevronLeft, Upload, FileText, User, Briefcase, Send, Loader, ShieldCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  type,
  ...props
}, ref) => {
  return <input type={type} className={`flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const Checkbox = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input type="checkbox" className={`peer h-5 w-5 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 accent-primary cursor-pointer ${className}`} ref={ref} {...props} />;
});
Checkbox.displayName = "Checkbox";
const STEPS = [{
  id: 1,
  title: "Referrer Details",
  icon: User
}, {
  id: 2,
  title: "Participant Info",
  icon: FileText
}, {
  id: 3,
  title: "Support Requirements",
  icon: Briefcase
}];
function ReferralFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    trigger
  } = useFormContext();
  const [currentStep, setCurrentStep] = useState(1);
  const progress = (currentStep - 1) / (STEPS.length - 1) * 100;
  const handleNext = async () => {
    let fieldsToValidate: string[] = [];
    if (currentStep === 1) {
      fieldsToValidate = ["referring_professional_name", "referring_professional_email", "organization_name"];
    } else if (currentStep === 2) {
      fieldsToValidate = ["client_name", "client_ndis_number", "client_dob", "client_post_code"];
    }
    const isValid = await trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length));
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  if (isSubmitted && !submitError) {
    return <div className="w-full max-w-2xl mx-auto py-12 px-4 animate-in fade-in zoom-in-95 duration-500">
        <Card className="border-green-100 bg-green-50/50 shadow-lg overflow-hidden">
          <div className="h-2 bg-green-500 w-full" />
          <CardContent className="pt-12 pb-12 flex flex-col items-center text-center space-y-6">
            <div className="h-24 w-24 rounded-full bg-green-100 flex items-center justify-center mb-2 shadow-sm">
              <Check className="h-12 w-12 text-green-600" />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-green-900">Referral Received</h3>
              <p className="text-green-800 max-w-md mx-auto leading-relaxed">{successMessage}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 pt-6 w-full justify-center">
              <Button onClick={() => {
              resetForm();
              setCurrentStep(1);
            }} variant="outline" className="border-green-200 hover:bg-green-100 text-green-800 min-w-[200px]">
                Submit Another Referral
              </Button>
              <Link to="/">
                <Button className="bg-green-700 hover:bg-green-800 text-white min-w-[200px]">
                  Return Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="w-full max-w-5xl mx-auto">
      <Card className="border-border/60 shadow-xl bg-card/95 backdrop-blur-sm overflow-hidden">
        <CardHeader className="pb-0 pt-8 px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
            <div className="space-y-1">
              <CardTitle className="text-2xl md:text-3xl font-bold text-primary tracking-tight">
                Professional Referral Form
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground max-w-2xl">
                Complete the steps below to refer a participant to Resolve Clinical Services.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full self-start md:self-center">
              <span className="text-sm font-medium text-primary">
                Step {currentStep} of {STEPS.length}
              </span>
            </div>
          </div>
          
          
          <div className="space-y-6 mb-8">
            <Progress value={progress} className="h-2.5 bg-muted" />
            <div className="flex justify-between text-xs md:text-sm font-medium text-muted-foreground px-1">
              {STEPS.map(step => <div key={step.id} className={`flex flex-col sm:flex-row items-center gap-2 transition-colors duration-300 ${currentStep >= step.id ? "text-primary font-semibold" : "opacity-50"}`}>
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300 ${currentStep >= step.id ? "bg-primary text-primary-foreground border-primary shadow-md" : "border-muted-foreground bg-background"}`}>
                    {currentStep > step.id ? <Check className="w-4 h-4" /> : <step.icon className="w-4 h-4" />}
                  </div>
                  <span className="text-center sm:text-left">{step.title}</span>
                </div>)}
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-6 md:px-10 py-8 bg-muted/5 border-t border-border/40">
          <Form onSubmit={handleSubmit} className="space-y-8">
            
            
            {currentStep === 1 && <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <FormField control={control} name="referring_professional_name" rules={{
                required: "Name is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Your Name <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="Dr. Jane Smith" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="referring_professional_email" rules={{
                required: "Email is required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address"
                }
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Your Email <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="jane.smith@clinic.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="referring_professional_phone" render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Your Phone Number</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="+61 8 1234 5678" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="organization_name" rules={{
                required: "Organization name is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Organisation / Practice <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="Bright Minds Therapy" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>
              </div>}

            
            {currentStep === 2 && <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                  <FormField control={control} name="client_name" rules={{
                required: "Client name is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Client Full Name <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="John Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="client_ndis_number" rules={{
                required: "NDIS number is required",
                minLength: {
                  value: 9,
                  message: "NDIS number seems too short"
                }
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Client NDIS Number <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="1234567890" {...field} />
                        </FormControl>
                        <FormDescription>Enter the 10‑digit NDIS participant number.</FormDescription>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="client_dob" rules={{
                required: "Date of birth is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Client Date of Birth <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <FormField control={control} name="client_post_code" rules={{
                required: "Postcode is required"
              }} render={({
                field
              }) => <FormItem>
                        <FormLabel className="text-base">Client Postcode <span className="text-destructive">*</span></FormLabel>
                        <FormControl>
                          <Input placeholder="5000" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField control={control} name="preferred_contact_method_face_to_face" render={({
                field
              }) => <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border border-input p-4 shadow-sm hover:bg-muted/50 transition-all cursor-pointer">
                        <FormControl>
                          <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)} />
                        </FormControl>
                        <FormLabel className="font-medium cursor-pointer w-full text-foreground">
                          Face-to-Face
                        </FormLabel>
                      </FormItem>} />
                  <FormField control={control} name="preferred_contact_method_telehealth" render={({
                field
              }) => <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border border-input p-4 shadow-sm hover:bg-muted/50 transition-all cursor-pointer">
                        <FormControl>
                          <Checkbox checked={field.value} onChange={e => field.onChange(e.target.checked)} />
                        </FormControl>
                        <FormLabel className="font-medium cursor-pointer w-full text-foreground">
                          Telehealth
                        </FormLabel>
                      </FormItem>} />
                </div>
                
                <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 flex gap-3 items-start">
                  <ShieldCheck className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Privacy & Consent</p>
                    <p>By proceeding, you confirm that you have obtained the participant's consent to share their details with Resolve Clinical Services for the purpose of this referral.</p>
                  </div>
                </div>
              </div>}

            
            {currentStep === 3 && <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-500">
                <FormField control={control} name="referral_reason" rules={{
              required: "Reason for referral is required"
            }} render={({
              field
            }) => <FormItem>
                      <FormLabel className="text-base">Reason for Referral <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Textarea placeholder="Briefly describe why you are referring this client..." className="min-h-[150px] resize-y" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="services_requested" render={() => <FormItem>
                      <div className="mb-4">
                        <FormLabel className="text-base">Services Requested</FormLabel>
                        
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {["Psychosocial Recovery Coaching", "NDIS Social Work", "NDIS Counselling"].map((item, i) => <FormField key={item} control={control} name="services_requested" render={({
                  field
                }) => {
                  return <FormItem key={item} data-index={i} className="flex flex-row items-center space-x-3 space-y-0 rounded-lg border border-input p-4 shadow-sm hover:bg-muted/50 hover:border-primary/30 transition-all cursor-pointer">
                                  <FormControl>
                                    <Checkbox checked={field.value?.includes(item)} onChange={e => {
                        const checked = e.target.checked;
                        const current = field.value || [];
                        const updated = checked ? [...current, item] : current.filter((value: string) => value !== item);
                        field.onChange(updated);
                      }} />
                                  </FormControl>
                                  <FormLabel className="font-medium cursor-pointer w-full text-foreground">
                                    {item}
                                  </FormLabel>
                                </FormItem>;
                }} />)}
                      </div>
                      <FormMessage />
                    </FormItem>} />

                <FormField control={control} name="supporting_documents" render={({
              field: {
                value,
                onChange,
                ...field
              }
            }) => <FormItem>
                      <FormLabel className="text-base">Upload Supporting Documents (optional)</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-center w-full">
                          <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-xl cursor-pointer bg-background hover:bg-muted/30 border-muted-foreground/25 hover:border-primary/50 transition-all group">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <div className="p-3 rounded-full bg-muted/30 group-hover:bg-primary/10 mb-3 transition-colors">
                                <Upload className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
                              </div>
                              <p className="mb-2 text-sm text-foreground font-medium">Click to upload or drag and drop</p>
                              <p className="text-xs text-muted-foreground">PDF, DOCX, or Images (MAX. 10MB)</p>
                            </div>
                            <Input type="file" className="hidden" onChange={e => {
                      onChange(e.target.files);
                    }} {...field} />
                          </label>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Attach any relevant reports, assessments, or consent forms.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>} />
              </div>}

            {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg animate-in fade-in">
                <p className="text-red-800 text-sm font-medium flex items-center gap-2">
                  <span className="text-lg"></span> {submitError}
                </p>
              </div>}

            <CardFooter className="flex justify-between px-0 pt-6 border-t border-border/50 mt-6">
              <Button type="button" variant="outline" onClick={handleBack} disabled={currentStep === 1 || isSubmitting} className="w-[120px] h-11">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {currentStep < STEPS.length ? <Button type="button" onClick={handleNext} className="w-[120px] h-11">
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button> : <Button type="submit" disabled={isSubmitting} className="w-[180px] h-11 bg-primary hover:bg-primary/90 shadow-md">
                  {isSubmitting ? <>
                      <Loader className="animate-spin mr-2 h-4 w-4" />
                      Submitting...
                    </> : <>
                      Submit Referral
                      <Send className="w-4 h-4 ml-2" />
                    </>}
                </Button>}
            </CardFooter>
          </Form>
        </CardContent>
      </Card>
    </div>;
}
export default function ProfessionalReferralForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="professional-referral-form" className="py-20 md:py-28 bg-background relative overflow-hidden">
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[800px] h-[800px] rounded-full bg-primary/5 blur-3xl opacity-60" />
        <div className="absolute top-[40%] -left-[10%] w-[600px] h-[600px] rounded-full bg-secondary/5 blur-3xl opacity-60" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 rounded-full bg-primary/10 text-primary text-sm font-medium">
            For Plan Managers & Support Coordinators
          </div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
            Make a Professional Referral
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            Use this secure portal to refer participants for Psychosocial Recovery Coaching and Capacity Building supports. We prioritize clear communication and rapid intake.
          </p>
        </div>

        <FormProvider formId="professional_referral_form_referrals" sectionName="referrals-section" defaultValues={{
        referring_professional_name: "",
        referring_professional_email: "",
        referring_professional_phone: "",
        organization_name: "",
        client_name: "",
        client_ndis_number: "",
        client_dob: "",
        client_post_code: "",
        preferred_contact_method_face_to_face: false,
        preferred_contact_method_telehealth: false,
        referral_reason: "",
        services_requested: [],
        supporting_documents: null
      }} validationRules={{
        referring_professional_name: {
          required: true
        },
        referring_professional_email: {
          required: true,
          pattern: "email"
        },
        organization_name: {
          required: true
        },
        client_name: {
          required: true
        },
        client_ndis_number: {
          required: true,
          minLength: 9
        },
        client_dob: {
          required: true
        },
        client_post_code: {
          required: true
        },
        referral_reason: {
          required: true
        }
      }} submitText="Submit Referral" successMessage="Thank you for your referral. We have received the details and will review them shortly. Our team will be in touch within 2 business days.">
          <ReferralFormContent />
        </FormProvider>
      </div>
    </section>;
}